import type React from "react";
import { useCallback, useRef, useState } from "react";
import { cn } from "@planta-v2/pds-ui/lib/utils";
import { i18n } from "@frontend/i18n";
import { DIRECTORY_INDEX_LETTERS } from "../../../domain/services/directory-letter-index.service";
import { DirectoryIndexBubble } from "./DirectoryIndexBubble";

const LAST_LETTER_INDEX = DIRECTORY_INDEX_LETTERS.length - 1;
const PAGE_KEY_STEP = 5;

interface DragState {
  readonly rect: DOMRect;
  index: number | null;
}

function nextIndexForKey(key: string, currentIndex: number): number | null {
  switch (key) {
    case "ArrowUp":
      return currentIndex - 1;
    case "ArrowDown":
      return currentIndex + 1;
    case "PageUp":
      return currentIndex - PAGE_KEY_STEP;
    case "PageDown":
      return currentIndex + PAGE_KEY_STEP;
    case "Home":
      return 0;
    case "End":
      return LAST_LETTER_INDEX;
    default:
      return null;
  }
}

interface DirectoryIndexRailProps {
  readonly availableLetters: ReadonlySet<string>;

  readonly onLetterChange: (letter: string) => void;
}

export function DirectoryIndexRail({
  availableLetters,
  onLetterChange,
}: DirectoryIndexRailProps): React.JSX.Element {
  const dragRef = useRef<DragState | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedLetter = DIRECTORY_INDEX_LETTERS[selectedIndex];

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      const drag = dragRef.current;
      if (drag === null) return;

      const slot = Math.floor(
        ((event.clientY - drag.rect.top) / drag.rect.height) *
          DIRECTORY_INDEX_LETTERS.length,
      );
      const index = Math.min(Math.max(slot, 0), LAST_LETTER_INDEX);
      const letter = DIRECTORY_INDEX_LETTERS[index];
      // Deduped on the letter: a raw drag fires ~120x a second, and each pass both
      // re-renders the bubble and makes useScrollRestoration hit sessionStorage.
      if (letter === undefined || index === drag.index) return;

      drag.index = index;
      setSelectedIndex(index);
      onLetterChange(letter);
    },
    [onLetterChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      const requested = nextIndexForKey(event.key, selectedIndex);
      if (requested === null) return;

      event.preventDefault();
      const index = Math.min(Math.max(requested, 0), LAST_LETTER_INDEX);
      const letter = DIRECTORY_INDEX_LETTERS[index];
      if (letter === undefined) return;

      setSelectedIndex(index);
      onLetterChange(letter);
    },
    [onLetterChange, selectedIndex],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      // Measured once per drag: background pages keep appending rows underneath, so
      // re-reading the rect per move would force a sync layout against the whole list.
      dragRef.current = {
        rect: event.currentTarget.getBoundingClientRect(),
        index: null,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsScrubbing(true);
      handlePointerMove(event);
    },
    [handlePointerMove],
  );

  const endScrub = useCallback((): void => {
    dragRef.current = null;
    setIsScrubbing(false);
  }, []);

  return (
    // One slider rather than 27 buttons: at ~22px a slot those targets miss the
    // WCAG 2.5.8 minimum, and they would sit as 27 tab stops before the list.
    <div
      role="slider"
      tabIndex={0}
      aria-orientation="vertical"
      aria-label={i18n.t("user.directory.index.railLabel")}
      aria-valuemin={0}
      aria-valuemax={LAST_LETTER_INDEX}
      aria-valuenow={selectedIndex}
      aria-valuetext={selectedLetter}
      data-testid="directory-index-rail"
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endScrub}
      onPointerCancel={endScrub}
      // `touch-none` has to be static: the browser commits to a pan at touchstart,
      // so a class added on pointerdown always arrives a frame too late.
      // Height is the letters' own (27 x 15px, as iOS sizes its index) capped to the
      // panel, and no padding: the scrub maps against this box, so it must be the letters.
      className="relative flex h-[min(100%,405px)] w-6 shrink-0 touch-none select-none flex-col items-center self-center outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
    >
      {DIRECTORY_INDEX_LETTERS.map((letter) => (
        <span
          key={letter}
          aria-hidden="true"
          data-testid={`directory-index-letter-${letter}`}
          className={cn(
            "flex min-h-0 flex-1 items-center text-pds-micro leading-none",
            availableLetters.has(letter)
              ? "text-muted-foreground"
              : "text-muted-foreground/30",
          )}
        >
          {letter}
        </span>
      ))}
      {isScrubbing && selectedLetter !== undefined && (
        <DirectoryIndexBubble letter={selectedLetter} />
      )}
    </div>
  );
}
